import { BanknoteX, Check, Clock3, Gift, Heart, Lock, MessageSquare, RotateCcw, Star, UserCheck } from "lucide-react";

/**
 * Dấu hiệu hiện trên một ô ảnh.
 *
 * `v2` là nhãn CHỮ (tấm đã có bản sửa), còn lại là biểu tượng.
 */
export type PhotoFlag =
  | "heart"
  | "lock"
  | "unpaid"
  | "staffPick"
  | "bareFace"
  | "star"
  | "v2"
  | "note"
  | "back"
  | "wait"
  | "done";

/**
 * Thứ tự CỐ ĐỊNH, cho cả 11 màn.
 *
 * Thứ tự đổi giữa các màn thì mắt người phải đọc lại từ đầu mỗi lần chuyển
 * màn — mà cả 11 màn dùng chung thành phần này chính là để khỏi phải đọc lại.
 *
 * Lựa chọn của khách / studio → TÌNH TRẠNG → số bản → lời dặn, đúng bản xem
 * thử CEO duyệt 26/09/2026 (tấm chờ duyệt: đồng hồ rồi "V2"; tấm bị trả lại:
 * mũi tên rồi lời dặn). Huy hiệu dồn về góc phải nên dấu CUỐI nằm sát mép.
 * Bản trước (lấy từ dự án PHOTO) đặt tình trạng cuối cùng — lệch bản duyệt.
 */
// `unpaid` đứng ngay sau `lock`: "khách đã chốt mua" rồi mới tới "nhưng chưa
// trả tiền" — hai dấu đọc liền nhau thành một câu (CEO chốt 3b, 05/10/2026).
// `staffPick` / `bareFace` (QĐ 0312, màn đơn ảnh thẻ): vẫn thuộc nhóm LỰA CHỌN
// — ai chọn tấm này, tấm này là quà — nên đứng sau cặp chốt/chưa trả tiền và
// trước sao của studio.
const ORDER: PhotoFlag[] = [
  "heart",
  "lock",
  "unpaid",
  "staffPick",
  "bareFace",
  "star",
  "back",
  "wait",
  "done",
  "v2",
  "note",
];

/**
 * Nền huy hiệu TÌNH TRẠNG dùng đặc màu, biểu tượng trắng.
 *
 * CEO chốt 26/09/2026 và đã bác HAI bản liên tiếp dùng viền/nền màu quanh ảnh
 * (nền tối; rồi nền nhạt + viền 1,5px): KHÔNG một vệt màu nào được chạm vào
 * mép ảnh. Tình trạng báo TRỌN ở đây.
 *
 * ⚠️ ĐỪNG dựng lại `padding` / `background` / `box-shadow inset` / dải
 * `::after` trên ô ảnh — đúng thứ đã gỡ hai lần.
 *
 * Đỏ `#B74949` là cùng một đỏ với nhãn đơn "gấp" (`features/orders/tags.ts`),
 * cố ý — cùng một ý "có chuyện", thì cùng một màu.
 */
const TONE: Partial<Record<PhotoFlag, string>> = {
  // `lock` là quyết định ĐÃ TÍNH TIỀN của khách, không phải một chặng công
  // việc — nên nó dùng màu thương hiệu chứ không ăn vào ba màu tình trạng.
  lock: "bg-[var(--color-brand)] text-white",
  // Nền trắng, hình đỏ: không trùng dấu nào trong ba màu tình trạng (đó là
  // CHẶNG việc của thợ), mà vẫn là đỏ "có chuyện" — chuyện ở đây là tiền.
  unpaid: "bg-white text-[#B74949]",
  back: "bg-[#B74949] text-white",
  wait: "bg-[#C9A96E] text-white",
  done: "bg-[#6B9E7A] text-white",
};

/**
 * Hai dấu LỰA CHỌN (tim của khách, sao của studio) vẽ biểu tượng ĐẶC có màu
 * trên nền tròn tối — đúng bản xem thử CEO duyệt 26/09/2026 (`.b.heart svg`,
 * `.b.star svg`: tô và viền cùng một màu). Bản trước vẽ nét trắng rỗng như
 * mọi dấu khác, nên tim và sao lẫn vào nhau khi nhìn nhanh cả lưới.
 */
const ICON_COLOR: Partial<Record<PhotoFlag, string>> = {
  heart: "#e0567a",
  star: "#e8b53a",
};

/** Lời cho người đọc màn hình — huy hiệu là hình tròn, không có chữ. */
const LABEL: Record<PhotoFlag, string> = {
  heart: "Khách thích",
  lock: "Khách đã chốt mua",
  unpaid: "Khách chưa trả tiền",
  staffPick: "Thợ chọn hộ",
  bareFace: "Mặt mộc · tặng kèm",
  star: "Studio đề xuất",
  v2: "Đã có bản sửa",
  note: "Có lời dặn",
  back: "Bị trả lại",
  wait: "Chờ duyệt",
  done: "Đã xong",
};

const ICON: Record<PhotoFlag, typeof Heart | null> = {
  heart: Heart,
  lock: Lock,
  unpaid: BanknoteX,
  staffPick: UserCheck,
  bareFace: Gift,
  star: Star,
  v2: null,
  note: MessageSquare,
  back: RotateCcw,
  wait: Clock3,
  done: Check,
};

/**
 * Chữ của huy hiệu phiên bản: "V3" khi biết tấm đang ở lần sửa thứ mấy.
 *
 * Bản đầu in cứng "V2" cho MỌI tấm đã sửa — kể cả tấm mới sửa một lần, hay đã
 * sửa tới lần ba. Bản xem thử ghi "V2" vì tấm mẫu đang ở lần 2; in cứng chữ
 * đó là nói sai với mọi tấm khác. Màn nào không biết số lần thì vẫn lùi về
 * "V2" như cũ để không đổi gì ở đó.
 */
function versionText(version?: number): string {
  return version && version > 0 ? `V${version}` : "V2";
}

/**
 * MỘT huy hiệu, không định vị — cho bảng chú giải dưới lưới và dòng "Tình
 * trạng" của panel chi tiết. Cùng hình, cùng màu với huy hiệu trên ô ảnh: chú
 * giải vẽ khác huy hiệu thật là dạy người đọc một ký hiệu không tồn tại.
 */
export function PhotoBadge({ flag, version }: { flag: PhotoFlag; version?: number }) {
  const Icon = ICON[flag];
  const tone = TONE[flag] ?? "bg-black/55 text-white";
  if (flag === "v2") {
    return (
      <span
        data-testid="badge-v2"
        title={LABEL[flag]}
        aria-label={LABEL[flag]}
        className={`grid h-[22px] min-w-[22px] place-items-center rounded-full px-1.5 text-[10.5px] font-bold ${tone}`}
      >
        {versionText(version)}
      </span>
    );
  }
  return (
    <span
      data-testid={`badge-${flag}`}
      title={LABEL[flag]}
      aria-label={LABEL[flag]}
      className={`grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full ${tone}`}
    >
      {Icon ? (
        <Icon
          className="h-[13px] w-[13px]"
          strokeWidth={2.4}
          aria-hidden
          {...(ICON_COLOR[flag]
            ? { style: { color: ICON_COLOR[flag], fill: ICON_COLOR[flag] } }
            : {})}
        />
      ) : null}
    </span>
  );
}

export function PhotoBadges({ flags, version }: { flags: PhotoFlag[]; version?: number }) {
  const shown = ORDER.filter((f) => flags.includes(f));
  if (shown.length === 0) return null;

  return (
    // pointer-events-none: huy hiệu là DẤU HIỆU, không phải nút. Một cú bấm
    // nhầm vào dấu tim là nói dối về lựa chọn của khách.
    <span className="pointer-events-none absolute right-[5px] top-[5px] flex gap-[3px]">
      {shown.map((f) => (
        <PhotoBadge key={f} flag={f} {...(version !== undefined ? { version } : {})} />
      ))}
    </span>
  );
}
